import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Interactive multi-arm portrait.
 * - New vignetted base image (blends seamlessly at edges into the section bg)
 * - Coloured variants cross-fade on top for hover reveals
 * - Soft brass glow sits BEHIND the person, so the arm stays fully prominent
 * - Service titles live in a side list, but each item is vertically anchored
 *   to its arm's y-position on the portrait — creating natural visual pairing
 */

const HOTSPOTS = [
  { id: "styling",   x: 47, y: 12, listY: 4,  label: "Personal Styling",        object: "The Brush",     desc: "Wardrobe design, personal shopping and event styling. A private stylist on call.",              n: "01", variant: "/assets/arm-styling-t.png" },
  { id: "brand",     x: 74, y: 25, listY: 20, label: "Brand Identity",          object: "The Dossier",   desc: "Visual language, mark, and storytelling — a house identity that carries across every touchpoint.", n: "02", variant: "/assets/arm-brand-t.png" },
  { id: "lifestyle", x: 55, y: 30, listY: 30, label: "Lifestyle Strategy",      object: "The Line",      desc: "An always‑on advisor for taste, travel and personal brand — on your terms.",                    n: "03", variant: "/assets/arm-lifestyle-t.png" },
  { id: "direction", x: 32, y: 40, listY: 42, label: "Creative Direction",      object: "The Pen",       desc: "For photoshoots, campaigns and brand development — a singular editor's eye across every frame.", n: "04", variant: "/assets/arm-direction-t.png" },
  { id: "content",   x: 15, y: 42, listY: 54, label: "Content & Communication", object: "The Notebook",  desc: "Narratives across print, digital and social — written in your voice, refined in ours.",         n: "05", variant: "/assets/arm-content-t.png" },
  { id: "gifting",   x: 84, y: 48, listY: 66, label: "Luxury Gifting",          object: "The Gift",      desc: "Curated hampers, bespoke packaging and gifting experiences — worthy of the moment.",             n: "06", variant: "/assets/arm-gifting-t.png" },
  { id: "events",    x: 50, y: 70, listY: 80, label: "Events & Experiences",    object: "The Measure",   desc: "Intimate gatherings and milestone celebrations — planned down to the millimetre.",              n: "07", variant: "/assets/arm-events-t.png" },
  { id: "interiors", x: 75, y: 84, listY: 92, label: "Interiors & Spaces",      object: "The Blueprint", desc: "Bespoke residences, second homes and private studios — considered down to the light switch.",  n: "08", variant: "/assets/arm-interiors-t.png" },
];

const BASE = "/assets/portrait-base-v2.png";

export default function PortraitServices() {
  const [active, setActive] = useState(HOTSPOTS[0].id);
  const [pinned, setPinned] = useState(false);
  const cycleRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (pinned) return;
    cycleRef.current = setInterval(() => {
      setActive((cur) => {
        const idx = HOTSPOTS.findIndex((h) => h.id === cur);
        return HOTSPOTS[(idx + 1) % HOTSPOTS.length].id;
      });
    }, 4600);
    return () => clearInterval(cycleRef.current);
  }, [pinned, active]);

  useEffect(() => {
    HOTSPOTS.forEach((h) => { const img = new Image(); img.src = h.variant; });
  }, []);

  const spot = HOTSPOTS.find((h) => h.id === active) || HOTSPOTS[0];
  const setEnter = (id) => setActive(id);
  const pin = (id) => { setActive(id); setPinned(true); };
  const unpin = () => setPinned(false);

  return (
    <section
      id="services"
      data-testid="portrait-services-section"
      className="relative overflow-hidden bg-onyx-deep"
      style={{
        background:
          "linear-gradient(180deg, #000000 0%, #030402 20%, #050604 60%, #070805 100%)",
      }}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-24 md:pb-32">
        {/* Section header */}
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-3 text-brass mb-6">
            <span className="w-8 h-px bg-brass/70" />
            <span className="text-[10px] md:text-[11px] tracking-overline uppercase font-medium">
              Services · The Studio, Personified
            </span>
          </div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-editorial leading-[0.98] text-cream">
            Lending a hand,<br />
            <span className="italic text-brass">meet our services.</span>
          </h2>
        </div>

        {/* Portrait | vertically-anchored side list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-start">
          {/* PORTRAIT CANVAS */}
          <div className="lg:col-span-8 relative">
            <div
              className="relative w-full mx-auto aspect-[3/2] select-none"
              data-testid="portrait-canvas"
              style={{
                WebkitMaskImage:
                  "radial-gradient(ellipse 88% 92% at 50% 48%, #000 45%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.25) 92%, rgba(0,0,0,0) 100%)",
                maskImage:
                  "radial-gradient(ellipse 88% 92% at 50% 48%, #000 45%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.25) 92%, rgba(0,0,0,0) 100%)",
              }}
            >
              {/* GLOW — sits BEHIND the base image, showing through the transparent bg regions */}
              <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                animate={{
                  background: `radial-gradient(circle at ${spot.x}% ${spot.y}%, rgba(200,165,103,0.45) 0%, rgba(176,140,74,0.24) 6%, rgba(176,140,74,0.10) 14%, rgba(0,0,0,0) 26%)`,
                }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                style={{ filter: "blur(20px)" }}
              />

              {/* Base — the new vignetted grayscale image; edges fade into section bg */}
              <img
                src={BASE}
                alt=""
                onLoad={() => setLoaded(true)}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
                draggable={false}
              />

              {/* Coloured variants — transparent-bg cutouts on top */}
              {HOTSPOTS.map((h) => {
                const isActive = h.id === active;
                return (
                  <img
                    key={h.id}
                    src={h.variant}
                    alt=""
                    aria-hidden
                    data-testid={`variant-${h.id}`}
                    className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none transition-opacity duration-[700ms]"
                    style={{ opacity: isActive ? 1 : 0 }}
                    draggable={false}
                  />
                );
              })}

              {/* Invisible hit areas */}
              {HOTSPOTS.map((h) => {
                const isActive = h.id === active;
                return (
                  <button
                    key={h.id}
                    data-testid={`hotspot-${h.id}`}
                    aria-label={h.label}
                    onMouseEnter={() => setEnter(h.id)}
                    onFocus={() => setEnter(h.id)}
                    onClick={() => (isActive && pinned ? unpin() : pin(h.id))}
                    className="absolute -translate-x-1/2 -translate-y-1/2 cursor-plus rounded-full"
                    style={{
                      left: `${h.x}%`,
                      top: `${h.y}%`,
                      width: "16%",
                      height: "16%",
                    }}
                  />
                );
              })}

              {/* Guide line — subtle brass line from active arm to right edge, drawing eye to the list */}
              <svg
                aria-hidden
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <motion.line
                  x1={spot.x}
                  y1={spot.y}
                  x2={100}
                  y2={spot.listY}
                  stroke="#B08C4A"
                  strokeWidth="0.12"
                  strokeDasharray="0.4 0.6"
                  opacity="0.55"
                  initial={false}
                  animate={{ x1: spot.x, y1: spot.y, y2: spot.listY }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </svg>
            </div>
          </div>

          {/* SIDE LIST — items vertically anchored to their arm's Y position */}
          <div
            className="lg:col-span-4 relative hidden lg:block"
            data-testid="service-list"
            style={{ aspectRatio: "3 / 4.5" }}
          >
            <div className="absolute inset-0">
              {HOTSPOTS.map((h) => {
                const isActive = h.id === active;
                return (
                  <div
                    key={h.id}
                    data-testid={`service-row-${h.id}`}
                    onMouseEnter={() => setEnter(h.id)}
                    onFocus={() => setEnter(h.id)}
                    onClick={() => (isActive && pinned ? unpin() : pin(h.id))}
                    tabIndex={0}
                    role="button"
                    aria-pressed={isActive}
                    className="group absolute left-0 right-0 cursor-pointer"
                    style={{
                      top: `${h.listY}%`,
                      transform: "translateY(-50%)",
                    }}
                  >
                    <div className="flex items-baseline gap-4">
                      {/* connector tick from portrait side */}
                      <motion.span
                        aria-hidden
                        className="block h-px bg-brass/50"
                        initial={false}
                        animate={{
                          width: isActive ? "24px" : "10px",
                          opacity: isActive ? 1 : 0.4,
                        }}
                        transition={{ duration: 0.5 }}
                      />
                      <span
                        className={`text-[10px] tracking-overline uppercase font-medium transition-colors duration-500 ${
                          isActive ? "text-brass" : "text-cream/40 group-hover:text-brass/70"
                        }`}
                      >
                        {h.n}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-serif tracking-editorial leading-none whitespace-nowrap overflow-visible transition-all duration-500 ease-out origin-left ${
                            isActive
                              ? "italic text-brass"
                              : "text-cream/55 group-hover:text-cream group-hover:italic"
                          }`}
                          style={{
                            fontSize: isActive
                              ? "clamp(24px, 2.2vw, 34px)"
                              : "clamp(13px, 1vw, 15px)",
                            transitionProperty: "font-size, color, font-style, letter-spacing",
                          }}
                        >
                          {h.label}
                        </div>

                        <AnimatePresence initial={false}>
                          {isActive && (
                            <motion.div
                              key={h.id + "-desc"}
                              initial={{ height: 0, opacity: 0, marginTop: 0 }}
                              animate={{
                                height: "auto",
                                opacity: 1,
                                marginTop: 10,
                                transition: {
                                  height: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                                  opacity: { duration: 0.4, delay: 0.1 },
                                  marginTop: { duration: 0.4 },
                                },
                              }}
                              exit={{
                                height: 0,
                                opacity: 0,
                                marginTop: 0,
                                transition: {
                                  height: { duration: 0.3 },
                                  opacity: { duration: 0.15 },
                                },
                              }}
                              className="overflow-hidden"
                              data-testid={`service-desc-${h.id}`}
                            >
                              <div className="text-[10px] tracking-overline uppercase italic font-serif text-cream/55 mb-1">
                                {h.object}
                              </div>
                              <p className="text-cream/72 text-[12px] md:text-[13px] leading-relaxed font-light max-w-[280px]">
                                {h.desc}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* MOBILE list — plain vertical stack */}
          <div className="lg:hidden mt-6">
            <ul className="divide-y divide-cream/10 border-y border-cream/10">
              {HOTSPOTS.map((h) => {
                const isActive = h.id === active;
                return (
                  <li
                    key={h.id}
                    data-testid={`service-row-m-${h.id}`}
                    onClick={() => (isActive && pinned ? unpin() : pin(h.id))}
                    className="py-3 px-1 cursor-pointer"
                  >
                    <div className="flex items-baseline gap-3">
                      <span className={`text-[10px] tracking-overline uppercase ${isActive ? "text-brass" : "text-cream/40"}`}>{h.n}</span>
                      <div
                        className={`font-serif tracking-editorial leading-tight transition-all duration-500 ${
                          isActive ? "italic text-brass text-2xl" : "text-cream/55 text-base"
                        }`}
                      >
                        {h.label}
                      </div>
                    </div>
                    {isActive && (
                      <p className="mt-2 text-cream/72 text-[13px] leading-relaxed font-light">{h.desc}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Progress + resume */}
        <div className="mt-8 md:mt-10 flex items-center gap-3 max-w-md ml-auto">
          <div className="flex-1 h-px bg-cream/10 relative overflow-hidden">
            <motion.div
              key={active + "-bar"}
              initial={{ width: pinned ? "100%" : "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: pinned ? 0.2 : 4.6, ease: "linear" }}
              className="h-full bg-brass"
            />
          </div>
          <span className="text-[10px] tracking-overline uppercase text-cream/40">
            {HOTSPOTS.findIndex((h) => h.id === active) + 1}/{HOTSPOTS.length}
          </span>
          {pinned && (
            <button
              onClick={unpin}
              data-testid="portrait-unpin"
              className="text-[10px] tracking-overline uppercase text-cream/60 link-underline"
            >
              Resume
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
